import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, API } from "../lib/api";
import { useT } from "../lib/i18n";
import { X, UploadSimple, Trash, PenNib, CheckCircle, Buildings, FloppyDisk, Image as ImageIcon } from "@phosphor-icons/react";

export const ASSET_ACCEPT = "image/png,image/jpeg,image/jpg";
export const MAX_ASSET_BYTES = 500 * 1024;
const ACCEPT = ASSET_ACCEPT;
const MAX_BYTES = MAX_ASSET_BYTES;

export const OPERATOR_TYPES = ["Manufacturer", "Importer", "Distributor", "Authorized Representative"];

function AssetPreview({ asset, alt, testid, emptyIcon, emptyText }) {
  if (asset) {
    return (
      <div className="border border-[#1E2C42] rounded-sm p-3 bg-[#0B1220]">
        <div className="bg-[#0E1626] border border-dashed border-[#2C3E5B] rounded-sm p-2 flex items-center justify-center min-h-[80px]">
          <img src={asset.data_url} alt={alt} className="max-h-16 object-contain" data-testid={testid} />
        </div>
      </div>
    );
  }
  return (
    <div className="border border-dashed border-[#2C3E5B] rounded-sm p-4 text-center bg-[#0B1220]">
      {emptyIcon}
      <div className="text-xs text-slate-400 mt-1">{emptyText}</div>
    </div>
  );
}

export default function SignatureUploader({ onClose, onChange, requiredCompany = false, onCompanySaved }) {
  const t = useT();
  const [sig, setSig] = useState(null);
  const [logo, setLogo] = useState(null);
  const [company, setCompany] = useState({
    company_name: "", company_address: "", company_country: "",
    economic_operator_type: "", eudamed_srn: "", responsible_person: "",
  });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [savingCo, setSavingCo] = useState(false);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const fileRef = useRef(null);
  const logoRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sigRes, profRes, logoRes] = await Promise.all([
        api.get("/user/signature"),
        api.get("/user/profile"),
        api.get("/user/logo"),
      ]);
      setSig(sigRes.data.has_signature ? sigRes.data : null);
      setLogo(logoRes.data.has_logo ? logoRes.data : null);
      setCompany({
        company_name: profRes.data.company_name || "",
        company_address: profRes.data.company_address || "",
        company_country: profRes.data.company_country || "",
        economic_operator_type: profRes.data.economic_operator_type || "",
        eudamed_srn: profRes.data.eudamed_srn || "",
        responsible_person: profRes.data.responsible_person || "",
      });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function uploadAsset(file, kind) {
    setErr(""); setOkMsg("");
    if (!ACCEPT.split(",").includes(file.type)) { setErr(t("sig.bad_type")); return; }
    if (file.size > MAX_BYTES) { setErr(t("sig.too_large")); return; }
    const setBusy = kind === "logo" ? setUploadingLogo : setUploading;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const url = kind === "logo" ? `${API}/user/logo` : `${API}/user/signature`;
      const res = await fetch(url, {
        method: "POST", body: form,
        headers: { Authorization: `Bearer ${localStorage.getItem("rg_token")}` },
        credentials: "include",
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || "Upload failed"); }
      setOkMsg(t("sig.uploaded"));
      await load();
      onChange?.();
    } catch (e) { setErr(e.message || "Upload failed"); }
    finally { setBusy(false); }
  }

  async function removeAsset(kind) {
    if (!window.confirm(t("sig.confirm_remove"))) return;
    await api.delete(kind === "logo" ? "/user/logo" : "/user/signature");
    setOkMsg("");
    await load();
    onChange?.();
  }

  async function saveCompany() {
    setErr("");
    setSavingCo(true);
    setOkMsg("");
    if (requiredCompany && ![
      company.company_name,
      company.company_address,
      company.company_country,
      company.economic_operator_type,
    ].every((value) => value?.trim())) {
      setErr(t("company.required_fields"));
      setSavingCo(false);
      return;
    }
    try {
      await api.put("/user/company", company);
      setOkMsg(t("sig.uploaded"));
      if (requiredCompany) await onCompanySaved?.();
      else onChange?.();
    } catch (error) {
      setErr(error.response?.data?.detail || error.message || t("company.save_error"));
    } finally { setSavingCo(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-6" onClick={onClose} data-testid="docmgmt-modal">
      <div className="bg-[#0E1626] border border-[#1E2C42] max-w-2xl w-full rounded-sm max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1E2C42] sticky top-0 bg-[#0E1626]">
          <div className="flex items-center gap-2">
            <Buildings size={20} weight="duotone" className="text-cyan-400" />
            <h2 className="text-lg font-extrabold tracking-tight">{t("docmgmt.title")}</h2>
          </div>
          <button data-testid="docmgmt-close" onClick={onClose} className="p-2 hover:bg-[#18243B] rounded-sm"><X size={18} /></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <p className="text-sm text-slate-400">{t("docmgmt.sub")}</p>

          {/* Company section */}
          <section>
            <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-3 inline-flex items-center gap-1">
              <Buildings size={12} /> {t("docmgmt.company_section")}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label={t("docmgmt.company_name")} v={company.company_name} on={(v) => setCompany({ ...company, company_name: v })} testid="co-name" required={requiredCompany} />
              <div>
                <label className="text-xs font-semibold uppercase text-slate-400">{t("docmgmt.operator_type")}</label>
                <select value={company.economic_operator_type}
                  onChange={(e) => setCompany({ ...company, economic_operator_type: e.target.value })}
                  required={requiredCompany}
                  data-testid="co-operator"
                  className="mt-1 w-full border border-[#1E2C42] px-2 py-1.5 rounded-sm text-sm bg-[#0E1626] focus:border-cyan-500 focus:outline-none">
                  <option value="">—</option>
                  {OPERATOR_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <Field label={t("docmgmt.company_address")} v={company.company_address} on={(v) => setCompany({ ...company, company_address: v })} testid="co-addr" required={requiredCompany} />
              <Field label={t("docmgmt.company_country")} v={company.company_country} on={(v) => setCompany({ ...company, company_country: v })} testid="co-country" required={requiredCompany} />
              <Field label={t("docmgmt.eudamed_srn")} v={company.eudamed_srn} on={(v) => setCompany({ ...company, eudamed_srn: v })} testid="co-eudamed" />
              <Field label={t("docmgmt.responsible_person")} v={company.responsible_person} on={(v) => setCompany({ ...company, responsible_person: v })} testid="co-prrc" />
            </div>
            <div className="flex justify-end pt-2">
              <button onClick={saveCompany} disabled={savingCo} data-testid="co-save"
                className="btn-primary inline-flex items-center gap-2 px-3 py-1.5 text-xs">
                <FloppyDisk size={12} weight="bold" /> {savingCo ? "…" : t("docmgmt.save_company")}
              </button>
            </div>
          </section>

          {/* Logo + Signature side-by-side */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Logo */}
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2 inline-flex items-center gap-1">
                <ImageIcon size={12} /> {t("docmgmt.logo_section")}
              </div>
              {loading ? <div className="text-xs font-mono text-slate-400">Loading…</div> : (
                <AssetPreview asset={logo} alt="logo" testid="logo-preview"
                  emptyIcon={<ImageIcon size={22} className="mx-auto text-slate-500" />} emptyText={t("docmgmt.no_logo")} />
              )}
              <input ref={logoRef} type="file" accept={ACCEPT} className="hidden" data-testid="logo-file-input"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAsset(f, "logo"); e.target.value = ""; }} />
              <div className="flex items-center gap-2 mt-2">
                <button onClick={() => logoRef.current?.click()} disabled={uploadingLogo} data-testid="logo-upload-btn"
                  className="btn-primary inline-flex items-center gap-1 px-3 py-1.5 text-xs">
                  <UploadSimple size={12} weight="bold" /> {uploadingLogo ? "…" : (logo ? t("docmgmt.replace") : t("docmgmt.upload"))}
                </button>
                {logo && (
                  <button onClick={() => removeAsset("logo")} data-testid="logo-remove-btn"
                    className="inline-flex items-center gap-1 border border-red-500/30 text-red-400 hover:bg-red-950/50 px-3 py-1.5 rounded-sm text-xs font-semibold transition-colors">
                    <Trash size={12} weight="bold" />
                  </button>
                )}
              </div>
            </div>
            {/* Signature */}
            <div>
              <div className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-2 inline-flex items-center gap-1">
                <PenNib size={12} /> {t("docmgmt.signature_section")}
              </div>
              {loading ? <div className="text-xs font-mono text-slate-400">Loading…</div> : (
                <AssetPreview asset={sig} alt="signature" testid="sig-preview"
                  emptyIcon={<PenNib size={22} className="mx-auto text-slate-500" />} emptyText={t("sig.none")} />
              )}
              <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" data-testid="sig-file-input"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAsset(f, "signature"); e.target.value = ""; }} />
              <div className="flex items-center gap-2 mt-2">
                <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="sig-upload-btn"
                  className="btn-primary inline-flex items-center gap-1 px-3 py-1.5 text-xs">
                  <UploadSimple size={12} weight="bold" /> {uploading ? t("sig.uploading") : (sig ? t("sig.replace_cta") : t("sig.upload_cta"))}
                </button>
                {sig && (
                  <button onClick={() => removeAsset("signature")} data-testid="sig-remove-btn"
                    className="inline-flex items-center gap-1 border border-red-500/30 text-red-400 hover:bg-red-950/50 px-3 py-1.5 rounded-sm text-xs font-semibold transition-colors">
                    <Trash size={12} weight="bold" />
                  </button>
                )}
              </div>
            </div>
          </section>

          {err && <div data-testid="sig-error" className="text-sm text-red-400 border border-red-500/30 bg-red-950/40 px-3 py-2">{err}</div>}
          {okMsg && <div data-testid="sig-ok" className="text-sm text-emerald-400 border border-emerald-500/30 bg-emerald-950/40 px-3 py-2 inline-flex items-center gap-2"><CheckCircle size={14} weight="fill" /> {okMsg}</div>}
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, on, testid, required = false }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase text-slate-400">{label}</label>
      <input value={v || ""} onChange={(e) => on(e.target.value)} data-testid={testid} required={required}
        className="mt-1 w-full border border-[#1E2C42] px-2 py-1.5 rounded-sm text-sm focus:border-cyan-500 focus:outline-none" />
    </div>
  );
}
